import { BackNextButton } from 'app/components/BackNextButton';
import React from 'react';
import styled from 'styled-components';
import { TextField } from 'app/components/TextField';
import {
  ApiError,
  GeneralLookup,
  LookupService,
  RegisterRider,
} from 'services/openapi';
import useApi from 'services/mavapi/useApi';
import AdminCustomButton from 'app/components/AdminCustomButton';

type Props = {
  tryItRegistration: RegisterRider;
  setTryItRegistration: React.Dispatch<React.SetStateAction<RegisterRider>>;
  onClickPlacementTwoBack?: () => void;
  handlePlacementSubmit?: (dataToSubmit?: RegisterRider) => void;
};

const NewMemberIndividualPlacementFormTwoNewRider = ({
  tryItRegistration,
  setTryItRegistration,
  onClickPlacementTwoBack,
  handlePlacementSubmit,
}: Props) => {
  const [associationLookUp, setAssociationLookUp] =
    React.useState<GeneralLookup[]>([]);
  const { error: apiError, handleRequest } = useApi();
  const [, setError] = React.useState<ApiError | unknown>();
  const { lookupGetAllLookups } = LookupService;
  const [isButtonDisabled, setIsButtonDisabled] = React.useState(true);

  function getAllLookupData(names?: string) {
    handleRequest(lookupGetAllLookups(names))
      .then(data => {
        if (data && data.length > 0) {
          // Assuming data is an array and you want the first item's values
          setAssociationLookUp(data[0]?.values || []);
        }
      })
      .catch((error) => {
        console.error('Error fetching lookup data:', error);
        setError(apiError || error);
      });
  }

  const handleFieldChange = (index: number, field: string, value: string) => {
    setTryItRegistration(prevData => {
      if (!prevData.riderAssociations) return prevData;

      const updatedRiderAssociations = [...prevData.riderAssociations];
      const currentAssociation = updatedRiderAssociations[index];
      const associationData = associationLookUp[index];

      // Only update the specific field that changed
      updatedRiderAssociations[index] = {
        ...currentAssociation,
        [field]: value,
        // Keep the association data consistent with the lookup
        association: {
          id: associationData?.id ?? '',
          name: associationData?.name ?? '',
          code: associationData?.code,
          children: associationData?.children,
          otherName: associationData?.otherName,
          isAdminLive: associationData?.isAdminLive,
        },
      };

      return { ...prevData, riderAssociations: updatedRiderAssociations };
    });
  };

  // Handle Skip button click - clear all associations and submit
  const handleSkipClick = () => {
    // Create updated rider data with empty associations
    const updatedRiderDataWithEmptyAssociations = {
      ...tryItRegistration,
      riderAssociations: [],
    };
    setTryItRegistration?.(updatedRiderDataWithEmptyAssociations);
    // Pass the updated data directly to parent's submit handler
    handlePlacementSubmit?.(updatedRiderDataWithEmptyAssociations);
  };
  React.useEffect(() => {
    getAllLookupData('ASSOCIATIONS');
  }, []);

  // Initialize riderAssociations if not present
  React.useEffect(() => {
    if (associationLookUp.length > 0 && (!tryItRegistration?.riderAssociations || tryItRegistration.riderAssociations.length === 0)) {
      const initialRiderAssociations = associationLookUp.map((association) => ({
        name: '',
        idNumber: '',
        association: {
          id: association.id ?? '',
          name: association.name ?? '',
          code: association.code,
          children: association.children,
          otherName: association.otherName,
          isAdminLive: association.isAdminLive,
        },
      }));

      setTryItRegistration(prevData => ({
        ...prevData,
        riderAssociations: initialRiderAssociations,
      }));
    }
  }, [associationLookUp, tryItRegistration?.riderAssociations, setTryItRegistration]);

  // Simplified validation effect
  React.useEffect(() => {
    if (!tryItRegistration?.riderAssociations) {
      setIsButtonDisabled(true);
      return;
    }

    const areAllMappingsValid = tryItRegistration.riderAssociations.every(
      value =>
        (value?.name && value?.idNumber) || (!value?.name && !value?.idNumber),
    );
    setIsButtonDisabled(!areAllMappingsValid);
  }, [tryItRegistration?.riderAssociations]);

  return (
    <MainWrapper className="form-MainWrapper">
      <OverflowWrapper className="OverflowWrapper">
        <p className="over-flow-header para-one">
          TO DOCUMENT PRIOR COMPETITION RECORDS:
        </p>
        <p className="over-flow-header para-two">
          <span>
            LIST YOUR NAME EXACTLY AS IT APPEARS IN ASSOCIATION RECORDS, AND{' '}
          </span>
          list your ID# for each association in which you have competed. (Not
          one in which you are simply a card holding member who has never
          participated in competition).
        </p>
        <MappedDataWrapper className="MappedDataWrapper">
          {associationLookUp?.map((value, index) => (
            <TextFieldGrid className="TextFieldGrid" key={`${value.id}-${index}`}>
              <p className="para-three">{value?.name}</p>
              <StyledNameField
                value={tryItRegistration?.riderAssociations?.[index]?.name || ''}
                onChange={e => handleFieldChange(index, 'name', e.target.value)}
                name={`name${index + 1}`}
                placeholder="name"
              />
              <StyledIdField
                value={tryItRegistration?.riderAssociations?.[index]?.idNumber || ''}
                onChange={e =>
                  handleFieldChange(index, 'idNumber', e.target.value)
                }
                name={`id${index + 1}`}
                placeholder="ID number"
              />
            </TextFieldGrid>
          ))}
        </MappedDataWrapper>

        <ButtonWrapper className="bg-color-change">
          <AdminCustomButton
            buttonType="cancel"
            onClick={handleSkipClick}
          >
            Skip
          </AdminCustomButton>
          <BackNextButton
            classNameBack="btn-back"
            classNameNxt="btn-nxt"
            onClickNxt={() => handlePlacementSubmit?.()}
            onClickBack={onClickPlacementTwoBack}
            next="Pay"
            back="Back"
            disableNext={isButtonDisabled}
          />
        </ButtonWrapper>
      </OverflowWrapper>
    </MainWrapper>
  );
};
const MainWrapper = styled.div`
  position: relative;
  height: calc(100vh - 230px);
  width: 100%;
  .para-one {
    color: #ff9900;
    font-size: 13px;
  }
  .para-two {
    color: #a7a7a7;
    font-size: 13px;
  }
  span {
    text-transform: uppercase;
  }
  @media (min-width: 576px) and (max-width: 767px) {
    .para-one {
      font-size: 11px;
    }
    .para-two {
      font-size: 11px;
    }
  }
  @media (min-width: 768px) and (max-width: 991px) {
    .para-one {
      font-size: 12px;
    }
    .para-two {
      font-size: 12px;
    }
  }
  @media (min-width: 992px) and (max-width: 1199px) {
    .para-one {
      font-size: 13px;
    }
    .para-two {
      font-size: 13px;
    }
  }
`;
const OverflowWrapper = styled.div`
  height: 100%;
  width: 100%;
  overflow: auto;
  padding-bottom: 100px;
  ::-webkit-scrollbar {
    width: 5px;
    margin-left: 3px;
  }
  ::-webkit-scrollbar-track {
    background: transparent !important;
    border-radius: 10px;
  }
  ::-webkit-scrollbar-thumb {
    width: 5px;
    background: transparent !important;
    border-radius: 10px;
  }
`;
const MappedDataWrapper = styled.div`
  height: calc(100vh - 225px);
  overflow: auto;
  padding-bottom: 100px;
  ::-webkit-scrollbar {
    width: 5px;
    margin-left: 3px;
  }
  ::-webkit-scrollbar-track {
    background: transparent !important;
    border-radius: 10px;
  }
  ::-webkit-scrollbar-thumb {
    width: 5px;
    background: transparent !important;
    border-radius: 10px;
  }
`;
const TextFieldGrid = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  width: 100%;
  padding-bottom: 20px;
  .para-three {
    flex: 0 0 calc(33% - 20px);
    font-size: 11px;
    color: #9597a6;
  }
  label {
    display: none !important;
  }
`;
const StyledNameField = styled(TextField)`
  display: flex;
  align-items: center;
  margin: 0px;
  flex: 0 0 calc(33% - 20px);
  input {
    width: 100%;
    height: 30px;
  }
`;
const StyledIdField = styled(TextField)`
  display: flex;
  align-items: center;
  flex: 0 0 calc(33% - 20px);
  margin: 0px;
  input {
    width: 100%;
    height: 30px;
  }
  ::placeholder {
  }
`;
const ButtonWrapper = styled.div`
  height: 70px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 0px 20px;

  &.bg-color {
    background: rgb(42 42 42) 100%;
  }
  &.bg-color-change {
    position: absolute;
    bottom: -15px;
    left: 0px;
    background: linear-gradient(
      to bottom,
      rgb(36 36 36 / 27%) 0%,
      rgb(42 42 42) 100%
    );
    border: 1px solid rgb(42 42 42);
    backdrop-filter: blur(5px);
  }
  @media (min-width: 100px) and (max-width: 575px) {
    &.bg-color-change {
      bottom: -2px;
    }
  }
  @media (min-width: 576px) and (max-width: 944px) {
    height: 45px;
    &.bg-color-change {
      bottom: -3px;
    }
  }
  @media (min-width: 945px) and (max-width: 1199px) {
    height: 50px;
    &.bg-color-change {
      bottom: -6px;
    }
  }
  @media (min-width: 1200px) and (max-width: 1365px) {
    height: 55px;
    &.bg-color-change {
      bottom: -6px;
    }
  }
  @media (min-width: 1366px) and (max-width: 1448px) {
    height: 70px;
    &.bg-color-change {
      bottom: -27px;
    }
  }
`;

export default NewMemberIndividualPlacementFormTwoNewRider;
